import React, { useEffect, useMemo } from 'react'
import type { PropsWithChildren } from 'react'

import { createInstance, MatomoProvider, useMatomo } from '@jonkoops/matomo-tracker-react'
import type { TrackEventParams } from '@jonkoops/matomo-tracker-react/es/types'
import type { Location } from 'history'
import type { HelmetData } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { getMatomoBaseUrl, getMatomoSiteId } from '../../runtimeConfig/runtimeConfig'

export const getInstance = (userId?: string) => {
  const urlBase = getMatomoBaseUrl()

  // A deployment with no Matomo host tracks nothing, so there is no instance to build.
  if (!urlBase) {
    return
  }
  return createInstance({
    urlBase,
    siteId: getMatomoSiteId(),
    userId: userId,
    disabled: false,
    heartBeat: {
      active: true,
      seconds: 10,
    },
    linkTracking: true,
    configurations: {
      setSecureCookie: true,
      setRequestMethod: 'POST',
    },
  })
}

export type UserTrackerProps = PropsWithChildren<{
  user?: { id?: string | null } | null
}>

const UserTracker = ({ user, children }: UserTrackerProps) => {
  // NB we can't just use `userProvider` here as we might be in a non broker-crm app
  const instance = useMemo(() => {
    return getInstance(user?.id || '')
  }, [user])

  if (!instance) {
    return <>{children}</>
  }
  return <MatomoProvider value={instance}>{children}</MatomoProvider>
}

const guidRegex = /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/g

export const filterPageHref = (location: Location) => {
  return location.pathname.replace(guidRegex, '_guid_')
}

const generateDimensions = (location: Location) => {
  const guids = [] as Array<{
    id: number
    value: string
  }>
  for (const id of location.pathname.matchAll(guidRegex)) {
    guids.push({
      id: 1,
      value: id[0],
    })
  }
  return guids
}

export type CustomDimension = {
  id: string
  value: string | number
}

export const trackEventFiltered = (
  trackEvent: (params: TrackEventParams) => void | undefined,
  location?: Location,
  category?: string,
  action?: string,
  name?: string,
  value?: number,
) => {
  if (location && !('Cypress' in window)) {
    const path = filterPageHref(location)
    trackEvent({
      href: path,
      category: category || 'unknown',
      action: action || 'unknown',
      name: name || undefined,
      value: value,
    })
  }
}

export const getFilteredPageTitle = ({ helmetData }: { helmetData: HelmetData }) => {
  // Helmet only fills `context.helmet` when it renders server-side; on the client it writes the
  // title straight to the document, so fall back to that rather than reporting an empty page name.
  // An empty context title falls back too - it carries no more information than no title at all.
  const helmetTitle = helmetData?.context.helmet?.title.toString()
  const title = helmetTitle || (typeof document === 'undefined' ? '' : document.title)
  return title ? title.replace(/<.*?>/g, '').replace(/\(.*\)/g, '') : ''
}

export type PageTrackerProps = PropsWithChildren<{ helmetData: HelmetData }>

export const PageTracker = ({ children, helmetData }: PageTrackerProps) => {
  const location = useLocation() as Location
  const { trackPageView } = useMatomo()
  useEffect(() => {
    if (location && !('Cypress' in window)) {
      const path = filterPageHref(location)
      const title = getFilteredPageTitle({ helmetData })
      trackPageView({
        href: path,
        documentTitle: title,
        customDimensions: generateDimensions(location),
      })
    }
  }, [helmetData, location, trackPageView])
  return <>{children}</>
}

export default UserTracker
